/**
 * BranchDaysSection — «Часы работы филиала» в панели расходников (ЕО и ОО), 05.10.2026.
 * Владелец: «поле, где указывается филиал, день и количество времени, пока он открыт: для
 * электроэнергии нужно просто посмотреть, сколько времени филиал работал».
 * Филиал × период (7/14/30 дней) → по каждому дню: с—по, сколько часов и откуда (по играм /
 * вручную / закрыт); сверху итог — часы и общий расход филиала в единицах и рублях (нормы блока
 * «Филиалы» в карточке расходника). Тап по дню: указать время, закрыт весь день, снова по играм.
 * Свёрнута по умолчанию: данные грузятся только при раскрытии.
 */
import { useMemo, useState } from "react";
import type { Branch } from "../../api/types";
import { BranchTabs } from "../../components/grid/BranchTabs";
import { Dropdown, TimePicker } from "../../components/shared";
import { fmtWorkMinutes } from "@ub/shared/branchDay";
import { addDaysIso } from "@ub/shared/dates";
import { useBranchDays, type BranchDay, type DayEdit } from "../../hooks/useBranchDays";
import { fmtDateApi, todayBusiness } from "../../utils/date";
import cfg from "./config.module.css";
import qcss from "./quests.module.css";
import dcss from "./dicts.module.css";
import css from "./BranchDays.module.css";

const PERIODS = [7, 14, 30];
const WD = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const dm = (iso: string) => `${WD[new Date(`${iso}T00:00:00Z`).getUTCDay()]} ${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
const n2 = (v: number) => (Math.round(v * 100) / 100).toLocaleString("ru-RU");
const SRC: Record<BranchDay["source"], string> = { games: "по играм", manual: "вручную", closed: "закрыт", none: "нет игр" };

const ClockIco = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
  </svg>
);

export function BranchDaysSection({ branches }: { branches: Branch[] }) {
  const [open, setOpen] = useState(false);
  const [br, setBr] = useState("");
  const [period, setPeriod] = useState(7);
  const [menu, setMenu] = useState<{ day: BranchDay; anchor: HTMLElement } | null>(null);
  const [pick, setPick] = useState<BranchDay | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const to = fmtDateApi(todayBusiness());
  const from = addDaysIso(to, -(period - 1));
  const branchId = branches.some((b) => b.id === br) ? br : branches[0]?.id ?? null;
  const bd = useBranchDays(branchId, from, to, open);
  const rows = useMemo(() => [...(bd.data?.days ?? [])].reverse(), [bd.data]);

  const apply = async (date: string, body: DayEdit) => {
    setHint(null);
    const e = await bd.setDay(date, body);
    if (e) setHint(`Не сохранилось: ${e}.`);
  };
  const choose = (id: string) => {
    const day = menu?.day;
    if (!day) return;
    if (id === "time") setPick(day);
    else void apply(day.date, id === "closed" ? { closed: true } : { clear: true });
  };

  return (
    <>
      <div className={`${qcss.s17head} ${dcss.headTap}`} onClick={() => setOpen((o) => !o)}>
        <span className={dcss.icy}><ClockIco /></span>
        <h2 className={`${cfg.secT} ${cfg.cy}`}>Часы работы филиала</h2>
        <span className={`${dcss.chev} ${dcss.cy} ${open ? dcss.up : ""}`}>▾</span>
      </div>
      {open ? (
        <div className={css.wrap}>
          <BranchTabs branches={branches} active={branchId ?? ""} onChange={setBr} allChip={false} upper={false} accent="role" />
          <div className={css.periods}>
            {PERIODS.map((n) => (
              <button key={n} type="button" className={`${dcss.fchip} ${period === n ? dcss.on : ""}`} onClick={() => setPeriod(n)}>
                <span className={dcss.fv}>{n} дней</span>
              </button>
            ))}
          </div>
          {bd.err ? <div className={cfg.errHint}>Не загрузилось: {bd.err}.</div> : null}
          {hint ? <div className={cfg.errHint}>{hint}</div> : null}
          {bd.data ? (
            <div className={css.sum}>
              <div className={css.sumRow}>
                <span className={css.nm}>Работал за {period} дней</span>
                <span className={css.val}>{fmtWorkMinutes(bd.data.minutes_total)}</span>
              </div>
              {bd.data.norms.map((n) => (
                <div key={n.consumable_id} className={css.sumRow}>
                  <span className={css.nm}>{n.name}</span>
                  <span className={css.val}>{n2(n.total)} {n.unit}</span>
                  <span className={css.rub}>{n.cost ? `₽${n2(n.sum)}` : "цена не задана"}</span>
                </div>
              ))}
              {bd.data.norms.length ? null : (
                <div className={css.hint}>
                  Общий расход не задан: откройте расходник (например, электроэнергию) и добавьте филиал
                  в блоке «Филиалы» — норму на час работы или на рабочий день.
                </div>
              )}
            </div>
          ) : bd.loading ? <div className={css.hint}>Загружаем…</div> : null}
          <div className={css.days}>
            {rows.map((d) => (
              <button key={d.date} type="button" className={css.day} onClick={(e) => setMenu({ day: d, anchor: e.currentTarget })}
                title={d.note || "Тап — поправить часы этого дня"}>
                <span className={css.d}>{dm(d.date)}</span>
                <span className={`${css.span} ${d.from ? "" : css.off}`}>{d.from && d.to ? `${d.from}–${d.to}` : "—"}</span>
                <span className={`${css.dur} ${d.minutes ? "" : css.off}`}>{d.minutes ? fmtWorkMinutes(d.minutes) : "не работал"}</span>
                <span className={`${css.src} ${d.source === "manual" ? css.manual : d.source === "closed" ? css.closed : ""}`}>
                  {SRC[d.source]}{d.source === "games" ? ` · ${d.games}` : ""}
                </span>
              </button>
            ))}
          </div>
          <div className={css.hint}>
            По умолчанию — от начала первой игры до конца последней (игра, чайная, мастер-класс, уборка).
            Ночные игры после полуночи относятся к своему вечеру. Сегодня — по записанным играм: итог ещё может измениться.
          </div>
        </div>
      ) : null}
      <Dropdown
        open={!!menu} anchor={menu?.anchor ?? null} multi={false} filter={false}
        title={menu ? dm(menu.day.date) : undefined}
        items={[
          { id: "time", label: "Указать время работы…" },
          { id: "closed", label: "Закрыт весь день" },
          ...(menu && (menu.day.source === "manual" || menu.day.source === "closed") ? [{ id: "clear", label: "Снова считать по играм" }] : []),
        ]}
        onClose={() => setMenu(null)}
        onCommit={(ids) => choose(String(ids[0] ?? ""))}
      />
      <TimePicker
        open={!!pick} mode="range" step={5} label={pick ? `Часы работы ${dm(pick.date)}` : "Часы работы"}
        value={pick ? `${pick.from ?? pick.auto?.from ?? "10:30"}–${pick.to ?? pick.auto?.to ?? "23:30"}` : undefined}
        onCommit={(v) => {
          const [a, b] = v.split(/[–-]/).map((x) => x.trim());
          const day = pick;
          setPick(null);
          if (day && a && b) void apply(day.date, { from: a, to: b });
        }}
        onCancel={() => setPick(null)}
      />
    </>
  );
}
